import { supabase } from "~/integrations/supabase/client";
import type {
  ConsumptionBySkuRow,
  CostingClosePeriodResult,
  CostingItemKind,
  CostingMovement,
  CostingMovementClass,
  CostingMovementDailyFgRow,
  CostingMovementDailyMpRow,
  CostingMovementInput,
  CostingMovementReportRow,
  CostingPeriod,
  CostingRecomputeResult,
  CostingSyncResult,
  OpeningImportResult,
  OpeningImportRow,
} from "~/types/costing";

const DOCUMENTS_BUCKET = "costing-documents";

// The daily movement RPCs were added after the last supabase types regeneration,
// so we call them through a thin typed wrapper (same approach as facturaCostsService).
type RpcReader = <TRow>(
  fn: string,
  args: Record<string, unknown>
) => Promise<{ data: TRow[] | null; error: { message: string } | null }>;

const rpcRead = supabase.rpc.bind(supabase) as unknown as RpcReader;

// Scalar-returning RPC wrapper for functions added after the last supabase types
// regeneration (e.g. costing_sync_adjustments).
type RpcCaller = <T>(
  fn: string,
  args: Record<string, unknown>
) => Promise<{ data: T | null; error: { message: string } | null }>;

const rpcCall = supabase.rpc.bind(supabase) as unknown as RpcCaller;

const ITEM_EMBED =
  "*, raw_materials(code, name, unit), oil_types(code, name), products(sku, short_name)";

export const costingService = {
  // ---------- Periods ----------

  async getPeriods(): Promise<CostingPeriod[]> {
    const { data, error } = await supabase
      .from("costing_periods")
      .select("*")
      .order("period", { ascending: false });

    if (error) throw error;
    return data as CostingPeriod[];
  },

  /** Resolve (and optionally create) a period by its YYYY-MM label. */
  async getOrCreatePeriod(period: string): Promise<string> {
    const { data, error } = await supabase.rpc("costing_get_period", {
      p_period: period,
      p_create: true,
    });

    if (error) throw error;
    return data as string;
  },

  // ---------- Movements (ledger) ----------

  async getMovements(
    periodId: string | string[],
    filters?: {
      itemKind?: CostingItemKind;
      movementClass?: CostingMovementClass | CostingMovementClass[];
    }
  ): Promise<CostingMovement[]> {
    let query = supabase
      .from("costing_movements")
      .select(ITEM_EMBED)
      .order("movement_date", { ascending: false })
      .order("created_at", { ascending: false });

    query = Array.isArray(periodId)
      ? query.in("period_id", periodId)
      : query.eq("period_id", periodId);

    if (filters?.itemKind) {
      query = query.eq("item_kind", filters.itemKind);
    }
    if (filters?.movementClass) {
      query = Array.isArray(filters.movementClass)
        ? query.in("movement_class", filters.movementClass)
        : query.eq("movement_class", filters.movementClass);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as unknown as CostingMovement[];
  },

  /** Manual entry (DTE purchase without PO, adjustment, waste, etc.). */
  async createMovement(input: CostingMovementInput): Promise<CostingMovement> {
    const { data, error } = await supabase
      .from("costing_movements")
      .insert({ ...input, source_type: "manual" })
      .select(ITEM_EMBED)
      .single();

    if (error) throw error;
    return data as unknown as CostingMovement;
  },

  async updateMovement(
    id: string,
    input: Partial<CostingMovementInput>
  ): Promise<CostingMovement> {
    const { data, error } = await supabase
      .from("costing_movements")
      .update(input)
      .eq("id", id)
      .select(ITEM_EMBED)
      .single();

    if (error) throw error;
    return data as unknown as CostingMovement;
  },

  async deleteMovement(id: string): Promise<void> {
    const { error } = await supabase
      .from("costing_movements")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },

  // ---------- Engine RPCs ----------

  async importOpening(
    period: string,
    rows: OpeningImportRow[]
  ): Promise<OpeningImportResult> {
    const { data, error } = await supabase.rpc("costing_import_opening", {
      p_period: period,
      p_rows: rows,
    });

    if (error) throw error;
    return data as unknown as OpeningImportResult;
  },

  async syncPurchases(period: string): Promise<CostingSyncResult> {
    const { data, error } = await supabase.rpc("costing_sync_purchases", {
      p_period: period,
    });
    if (error) throw error;
    return data as CostingSyncResult;
  },

  async syncConsumption(period: string): Promise<CostingSyncResult> {
    const { data, error } = await supabase.rpc("costing_sync_consumption", {
      p_period: period,
    });
    if (error) throw error;
    return data as CostingSyncResult;
  },

  async syncProduction(period: string): Promise<CostingSyncResult> {
    const { data, error } = await supabase.rpc("costing_sync_production", {
      p_period: period,
    });
    if (error) throw error;
    return data as CostingSyncResult;
  },

  async syncSales(period: string): Promise<CostingSyncResult> {
    const { data, error } = await supabase.rpc("costing_sync_sales", {
      p_period: period,
    });
    if (error) throw error;
    return data as CostingSyncResult;
  },

  /** Ingest stock adjustments (ingredient ledger + WMS product adjustments) as
   *  costing 'adjustment' movements so they show in the Costeo reports. */
  async syncAdjustments(period: string): Promise<CostingSyncResult> {
    const { data, error } = await rpcCall<CostingSyncResult>(
      "costing_sync_adjustments",
      { p_period: period }
    );
    if (error) throw new Error(error.message);
    return data ?? {};
  },

  /** Sync everything from operational data, then recompute WAC. */
  async syncAll(period: string): Promise<{
    purchases: CostingSyncResult;
    consumption: CostingSyncResult;
    production: CostingSyncResult;
    sales: CostingSyncResult;
    adjustments: CostingSyncResult;
    recompute: CostingRecomputeResult;
  }> {
    const purchases = await this.syncPurchases(period);
    const consumption = await this.syncConsumption(period);
    const production = await this.syncProduction(period);
    const sales = await this.syncSales(period);
    const adjustments = await this.syncAdjustments(period);
    const recompute = await this.recomputeWac(period);
    return { purchases, consumption, production, sales, adjustments, recompute };
  },

  async recomputeWac(period: string): Promise<CostingRecomputeResult> {
    const { data, error } = await supabase.rpc("costing_recompute_wac", {
      p_period: period,
    });
    if (error) throw error;
    return data as unknown as CostingRecomputeResult;
  },

  async closePeriod(period: string): Promise<CostingClosePeriodResult> {
    const { data, error } = await supabase.rpc("costing_close_period", {
      p_period: period,
    });
    if (error) throw error;
    return data as unknown as CostingClosePeriodResult;
  },

  // ---------- Movement report (waterfall view) ----------

  async getMovementReport(
    period: string,
    itemKind?: CostingItemKind | CostingItemKind[]
  ): Promise<CostingMovementReportRow[]> {
    let query = supabase
      .from("v_costing_movement_report")
      .select("*")
      .eq("period", period)
      .order("item_name", { ascending: true });

    if (itemKind) {
      query = Array.isArray(itemKind)
        ? query.in("item_kind", itemKind)
        : query.eq("item_kind", itemKind);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as unknown as CostingMovementReportRow[];
  },

  // ---------- Daily movement drill-down (moving-average "Días" view) ----------

  /** Per-day moving-average waterfall for a finished good. */
  async getMovementDailyFg(
    period: string,
    productId: string
  ): Promise<CostingMovementDailyFgRow[]> {
    const { data, error } = await rpcRead<CostingMovementDailyFgRow>(
      "costing_movement_daily_fg",
      { p_period: period, p_product_id: productId }
    );
    if (error) throw new Error(error.message);
    return data ?? [];
  },

  /** Per-day moving-average waterfall for an ingredient (raw material / oil). */
  async getMovementDailyMp(
    period: string,
    itemId: string
  ): Promise<CostingMovementDailyMpRow[]> {
    const { data, error } = await rpcRead<CostingMovementDailyMpRow>(
      "costing_movement_daily_mp",
      { p_period: period, p_item_id: itemId }
    );
    if (error) throw new Error(error.message);
    return data ?? [];
  },

  // ---------- Consumo por SKU (attribution report) ----------

  /** Period ingredient consumption attributed to each finished product (SKU),
   *  valued at real WAC. One row per (ingredient × product). */
  async getConsumptionBySku(period: string): Promise<ConsumptionBySkuRow[]> {
    const { data, error } = await rpcRead<ConsumptionBySkuRow>(
      "get_consumption_by_sku",
      { p_period: period }
    );
    if (error) throw new Error(error.message);
    return data ?? [];
  },

  // ---------- Documents (reception / DTE PDFs) ----------

  async uploadDocument(file: File, period: string): Promise<string> {
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${period}/${Date.now()}_${safeName}`;

    const { error } = await supabase.storage
      .from(DOCUMENTS_BUCKET)
      .upload(path, file, { contentType: file.type || "application/pdf" });

    if (error) throw error;
    return path;
  },

  /** Signed URL for the in-app PDF viewer (vital for discrepancy analysis). */
  async getDocumentUrl(path: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from(DOCUMENTS_BUCKET)
      .createSignedUrl(path, 60 * 60);

    if (error) throw error;
    return data.signedUrl;
  },
};
